import React, { Dispatch, SetStateAction } from "react";
import { Grid } from "@mui/material";
// @ts-ignore
import { StriimTypography, StriimList, StriimListItem, StriimCheckbox } from "@striim/striim-ui";

import SchemasListSingleSelection from "./schemas-list-single-selection";
import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";
import { styles } from "./schemas-list.styles";
import { Schema } from "../../schema-table";

interface SchemaListProps {
    schemas: Schema[];
    setSchemas: Dispatch<SetStateAction<Schema[]>>;
    schemaType: string;
    selectedSchema?: Schema;
    setSelectedSchema: Dispatch<SetStateAction<Schema>>;
    isMultiSelectionAllowed?: boolean;
}

const SchemaList: React.FC<SchemaListProps> = ({
    schemas,
    setSchemas,
    schemaType,
    selectedSchema,
    setSelectedSchema,
    isMultiSelectionAllowed = true
}) => {
    const currentSelection = selectedSchema ?? schemas[0];

    const handleSchemaSelection = (name: string, value: boolean) => {
        let updatedCurrentSelection: Schema | undefined;
        const updatedSchemas = schemas.map(schema => {
            const isMatchingSchmea = schema.name === name;
            const updatedSchema = {
                ...schema,
                isSelected: (isMatchingSchmea ? value : schema.isSelected) ?? false,
                tables:
                    (isMatchingSchmea
                        ? [...schema?.tables].map(table => ({ ...table, isSelected: value }))
                        : schema.tables) ?? []
            };
            if (schema.name === currentSelection.name) {
                updatedCurrentSelection = updatedSchema;
            }
            return updatedSchema;
        });
        setSchemas(updatedSchemas);
        setSelectedSchema(updatedCurrentSelection);
    };

    return (
        <Grid display="flex" direction="column" width={"100%"} sx={styles.schemaListContainer}>
            <StriimList sx={styles.listWrapper}>
                {!isMultiSelectionAllowed ? (
                    <SchemasListSingleSelection
                        schemas={schemas}
                        currentSelection={currentSelection}
                        selectedSchema={selectedSchema}
                        setSelectedSchema={setSelectedSchema}
                    />
                ) : schemas?.length ? (
                    schemas.map(schema => {
                        const tablesCount = schema.tables?.length || 0;
                        const selectedTablesCount = schema.tables?.filter(table => !!table.isSelected)?.length || 0;
                        const areSomeTablesUnselected = selectedTablesCount && tablesCount !== selectedTablesCount;
                        const isSchemaSelected = schema.name === currentSelection?.name;

                        return (
                            <StriimListItem
                                key={schema.name}
                                selected={schema.name === currentSelection?.name}
                                divider={false}
                                sx={isSchemaSelected ? styles.schemaSelected : ""}
                                onClick={() => setSelectedSchema(schema)}
                                data-test-id={`select-${schema.name}-checkbox`}
                            >
                                <Grid
                                    container
                                    flexDirection="column"
                                    p={0.2}
                                    pl={1.2}
                                    sx={(styles.schemaListItem, styles.schemaListItemSelected)}
                                >
                                    <Grid container alignItems={"center"} gap={1} flexWrap={"nowrap"}>
                                        <StriimCheckbox
                                            checked={schema.isSelected}
                                            indeterminate={areSomeTablesUnselected}
                                            onChange={event => {
                                                handleSchemaSelection(schema.name, event.target.checked);
                                            }}
                                        />
                                        <EllipsisText
                                            text={schema.name}
                                            sx={{
                                                ...styles.schemaItem,
                                                ...(isSchemaSelected ? styles.schemaListItemSelected : styles.schemaListItem)
                                            }}
                                        />
                                    </Grid>
                                    <Grid container pl={3.6}>
                                        <StriimTypography
                                            variant="caption3"
                                            sx={
                                                isSchemaSelected ? styles.schemaListItemSelected : styles.schemaListItem
                                            }
                                        >
                                            {selectedTablesCount}/{tablesCount} selected
                                        </StriimTypography>
                                    </Grid>
                                </Grid>
                            </StriimListItem>
                        );
                    })
                ) : (
                    <Grid container height={"100%"} justifyContent="center" alignItems="center">
                        <StriimTypography variant="body">No {schemaType}s available</StriimTypography>
                    </Grid>
                )}
            </StriimList>
        </Grid>
    );
};

export default SchemaList;
